import { __decorate } from "tslib";
import { provide } from '@lit/context';
import { localized, msg } from '@lit/localize';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { createValidity, submitForm } from '../../shared/forms/form-utils.js';
import { HelixValidatable } from '../../shared/forms/helix-validatable.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import '../icon/icon.js';
import { RadioGroupContext, radioGroupAutocompleteContext, radioGroupContext, radioGroupDisabledContext, radioGroupNameContext, radioGroupReadonlyContext, radioGroupValueContext, } from './radio-group-context.js';
import { radioGroupStyles } from './radio-group.styles.js';
import { FormDataController } from '../../shared/forms/form-data.controller.js';
let HlxRadioGroup = class HlxRadioGroup extends HelixValidatable {
    constructor() {
        super();
        this.options = [];
        this.context = new RadioGroupContext();
        this.label = '';
        this.description = '';
        this.name = '';
        this.value = undefined;
        /** Is the form control required to be filled/checked */
        this.required = false;
        this.type = 'column';
        this.helpText = '';
        this.disabled = false;
        this.readonly = false;
        this.role = 'radiogroup';
        this.slotController = new SlotController(this, 'label', 'description');
        new FormDataController(this);
        this.context.onSelect((option) => {
            this.value = option.value;
            this.emit('hlx-change');
            if (this.validity.valid) {
                this.validation.showError = false;
            }
            this.assignTabIndices();
        });
        this.context.onAdd((option) => {
            this.options.push(option);
            this.assignTabIndices();
        });
        this.context.onRemove((option) => {
            this.options.splice(this.options.indexOf(option), 1);
            this.assignTabIndices();
        });
    }
    render() {
        const showLabel = Boolean(this.label) ||
            this.slotController.hasSlot('label') ||
            Boolean(this.description) ||
            this.slotController.hasSlot('description');
        return html `
            <div
                role="presentation"
                part="hlx-form-control"
                class="${classMap({
            ...this.validation.classMap,
            'hlx-form-control-md': true,
            'hlx-form-control-disabled': this.disabled,
            'hlx-form-control-has-label': showLabel,
            'hlx-form-control-has-help-text': Boolean(this.helpText),
        })}"
                ?disabled=${this.disabled}
                @keydown=${this.handleKeydown}>
                <div class="legend" role="presentation">
                    <slot part="hlx-form-control-label" class="hlx-form-control-label" name="label">
                        ${this.label}
                    </slot>
                    <slot
                        part="hlx-form-control-description"
                        class="hlx-form-control-description"
                        name="description">
                        ${this.description}
                    </slot>
                </div>

                <slot
                    part="base"
                    class="${classMap({
            'hlx-radio-group': true,
            'hlx-radio-group-column': this.type === 'column',
            'hlx-radio-group-row': this.type === 'row',
            'hlx-radio-group-disabled': this.disabled,
        })}"></slot>

                ${this.validation.renderErrorSlot('hlx-radio-group-error')}
            </div>
        `;
    }
    get validity() {
        if (this.validation.hasCustomValidity) {
            return createValidity('customError');
        }
        if (this.required && !this.value) {
            return createValidity('valueMissing');
        }
        return createValidity();
    }
    focus(options) {
        const option = this.options.find((o) => o.value === this.value) || this.options[0];
        option?.focus(options);
    }
    getDefaultErrorMessage(validity) {
        if (validity.valueMissing) {
            return msg('Please select one of these options.');
        }
        return '';
    }
    handleKeydown(e) {
        const options = this.getAvailableOptions();
        const current = options.indexOf(document.activeElement);
        if (current == -1) {
            return;
        }
        if (e.code === 'ArrowUp' || e.code === 'ArrowLeft') {
            this.selectOption(current - 1);
            e.preventDefault();
        }
        else if (e.code === 'ArrowDown' || e.code === 'ArrowRight') {
            this.selectOption(current + 1);
            e.preventDefault();
        }
        else if (e.key === 'Enter') {
            submitForm(this.getForm());
        }
    }
    selectOption(optionIdx) {
        const options = this.getAvailableOptions();
        let nextOption = options[optionIdx];
        if (!nextOption) {
            nextOption = optionIdx < 0 ? options[options.length - 1] : options[0];
        }
        nextOption.focus();
        nextOption.select();
    }
    getAvailableOptions() {
        return this.options.filter((o) => !(o.disabled || o.disabled === ''));
    }
    assignTabIndices() {
        const options = this.getAvailableOptions();
        let activeOption = options.find((o) => o.value === this.value);
        if (!activeOption) {
            activeOption = options[0];
        }
        this.options.forEach((option) => {
            if (option === activeOption) {
                option.tabIndex = 0;
            }
            else {
                option.tabIndex = -1;
            }
        });
    }
};
HlxRadioGroup.styles = [componentStyles, formControlStyles, radioGroupStyles];
__decorate([
    provide({ context: radioGroupContext })
], HlxRadioGroup.prototype, "context", void 0);
__decorate([
    property({ type: String })
], HlxRadioGroup.prototype, "label", void 0);
__decorate([
    property({ type: String })
], HlxRadioGroup.prototype, "description", void 0);
__decorate([
    provide({ context: radioGroupNameContext }),
    property({ type: String })
], HlxRadioGroup.prototype, "name", void 0);
__decorate([
    provide({ context: radioGroupValueContext }),
    property({ type: String })
], HlxRadioGroup.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxRadioGroup.prototype, "required", void 0);
__decorate([
    property({ type: String })
], HlxRadioGroup.prototype, "type", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxRadioGroup.prototype, "helpText", void 0);
__decorate([
    provide({ context: radioGroupDisabledContext }),
    property({ type: Boolean, reflect: true })
], HlxRadioGroup.prototype, "disabled", void 0);
__decorate([
    provide({ context: radioGroupReadonlyContext }),
    property({ type: Boolean, reflect: true })
], HlxRadioGroup.prototype, "readonly", void 0);
__decorate([
    provide({ context: radioGroupAutocompleteContext }),
    property({ type: String, reflect: true })
], HlxRadioGroup.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxRadioGroup.prototype, "role", void 0);
HlxRadioGroup = __decorate([
    customElement('hlx-radio-group'),
    localized()
], HlxRadioGroup);
export { HlxRadioGroup };
//# sourceMappingURL=radio-group.js.map